import { UserPreferencesConfig } from 'softcat:config/user-preferences.ts';
import { SoftcatApiLocation } from 'softcat:types/softcat-api.ts';
import { SearchParameterUtils } from 'softcat:utils/search-parameters.ts';
import { SoftcatApiUtils } from 'softcat:utils/softcat-api.ts';

const create = async (data: Partial<SoftcatApiLocation>): Promise<SoftcatApiLocation> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.post<SoftcatApiLocation>(`/locations?${parameters}`, { body: { ...data, territory } });
};

type FindAllParameters = { limit?: number; orderBy?: string; page?: number; query?: string };

const buildFindAllSearchParameters = ({ limit = 10, orderBy = '', page = 0, query = '' }: FindAllParameters, nextToken?: string) => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);

    return new URLSearchParams(
        SearchParameterUtils.removeEmptySearchParameters({
            filter: `territory=${territory}${query ? `,name==${query}*` : ''}`,
            limit: `${limit}`,
            nextToken: nextToken ?? '',
            offset: nextToken === undefined ? `${page}` : '',
            orderBy: `${orderBy}`,
            territory: `${territory}`,
        }),
    );
};

const findAll = async (parameters: FindAllParameters = {}): Promise<SoftcatApiLocation[]> => {
    return SoftcatApiUtils.get<SoftcatApiLocation[]>(`/locations?${buildFindAllSearchParameters(parameters)}`);
};

const findAllWithNextToken = async ({ nextToken, ...parameters }: FindAllParameters & { nextToken?: string } = {}): Promise<{
    locations: SoftcatApiLocation[];
    nextToken: string | undefined;
}> => {
    const { data, headers } = await SoftcatApiUtils.getWithHeaders<SoftcatApiLocation[]>(`/locations?${buildFindAllSearchParameters(parameters, nextToken)}`);

    return { locations: data, nextToken: headers.get('X-Next-Token') ?? undefined };
};

const findOne = async ({ name }: { name: string }): Promise<SoftcatApiLocation> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.get<SoftcatApiLocation>(`/locations/${name}?${parameters}`);
};

const remove = async ({ name }: { name: string }): Promise<void> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.remove(`/locations/${name}?${parameters}`);
};

const update = async (data: Partial<SoftcatApiLocation>): Promise<SoftcatApiLocation> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.put<SoftcatApiLocation>(`/locations/${data.name}?${parameters}`, { body: data });
};

export const LocationService = {
    create,
    findAll,
    findAllWithNextToken,
    findOne,
    remove,
    update,
};
